import { useState, useEffect } from 'react';
import api from '../services/api';
import '../styles/Settings.css';

export default function Settings() {
  const [settings, setSettings] = useState({
    whatsapp_phone_number_id: '',
    whatsapp_access_token: '',
    whatsapp_webhook_token: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showTokens, setShowTokens] = useState({
    token: false,
    webhook: false
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const phoneId = await api.get('/settings/whatsapp_phone_number_id');
      const token = await api.get('/settings/whatsapp_access_token');
      const webhook = await api.get('/settings/whatsapp_webhook_token');

      setSettings({
        whatsapp_phone_number_id: phoneId.data?.value || '',
        whatsapp_access_token: token.data?.value || '',
        whatsapp_webhook_token: webhook.data?.value || ''
      });
      setLoading(false);
    } catch (err) {
      console.log('Settings not configured yet (first time)');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings({ ...settings, [name]: value });
    setError('');
  };

  const handleSave = async (key) => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const value = settings[key];
      
      if (!value || value.trim() === '') {
        setError(`${key} cannot be empty`);
        setSaving(false);
        return;
      }

      await api.patch(`/settings/${key}`, { value });
      setSuccess(`${key.replace(/_/g, ' ')} saved successfully!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save setting');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (!settings.whatsapp_phone_number_id) {
        setError('Phone Number ID required');
        setSaving(false);
        return;
      }
      if (!settings.whatsapp_access_token) {
        setError('Access Token required');
        setSaving(false);
        return;
      }
      if (!settings.whatsapp_webhook_token) {
        setError('Webhook Token required');
        setSaving(false);
        return;
      }

      await api.patch('/settings/whatsapp_phone_number_id', { value: settings.whatsapp_phone_number_id });
      await api.patch('/settings/whatsapp_access_token', { value: settings.whatsapp_access_token });
      await api.patch('/settings/whatsapp_webhook_token', { value: settings.whatsapp_webhook_token });

      setSuccess('All WhatsApp settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="settings-container"><p>Loading settings...</p></div>;

  return (
    <div className="settings-container">
      <h1>⚙️ Settings</h1>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="settings-section">
        <h2>💬 WhatsApp API Configuration</h2>
        <p className="section-desc">Configure your Meta WhatsApp Business Account credentials to enable automated messages</p>

        <div className="settings-form">
          <div className="form-group">
            <label>Phone Number ID *</label>
            <input
              type="text"
              name="whatsapp_phone_number_id"
              value={settings.whatsapp_phone_number_id}
              onChange={handleChange}
              placeholder="1234567890123456"
              className="form-input"
            />
            <small>Get from Meta App Dashboard → WhatsApp → Phone Numbers</small>
            <button 
              type="button" 
              className="btn btn-sm btn-info"
              onClick={() => handleSave('whatsapp_phone_number_id')}
              disabled={saving}
            >
              Save
            </button>
          </div>

          <div className="form-group">
            <label>Access Token *</label>
            <div className="token-input-wrapper">
              <input
                type={showTokens.token ? 'text' : 'password'}
                name="whatsapp_access_token"
                value={settings.whatsapp_access_token}
                onChange={handleChange}
                placeholder="EAA..."
                className="form-input"
              />
              <button
                type="button"
                className="btn-toggle-visibility"
                onClick={() => setShowTokens({ ...showTokens, token: !showTokens.token })}
              >
                {showTokens.token ? '🙈 Hide' : '👁️ Show'}
              </button>
            </div>
            <small>Get from Meta App Dashboard → Settings → User Token (must have whatsapp_business_messaging permission)</small>
            <button 
              type="button" 
              className="btn btn-sm btn-info"
              onClick={() => handleSave('whatsapp_access_token')}
              disabled={saving}
            >
              Save
            </button>
          </div>

          <div className="form-group">
            <label>Webhook Token *</label>
            <div className="token-input-wrapper">
              <input
                type={showTokens.webhook ? 'text' : 'password'}
                name="whatsapp_webhook_token"
                value={settings.whatsapp_webhook_token}
                onChange={handleChange}
                placeholder="your_webhook_verification_token"
                className="form-input"
              />
              <button
                type="button"
                className="btn-toggle-visibility"
                onClick={() => setShowTokens({ ...showTokens, webhook: !showTokens.webhook })}
              >
                {showTokens.webhook ? '🙈 Hide' : '👁️ Show'}
              </button>
            </div>
            <small>Set any secure string. Use this when registering webhook in Meta App Dashboard</small>
            <button 
              type="button" 
              className="btn btn-sm btn-info"
              onClick={() => handleSave('whatsapp_webhook_token')}
              disabled={saving}
            >
              Save
            </button>
          </div>

          <div className="form-actions">
            <button 
              className="btn btn-primary"
              onClick={handleSaveAll}
              disabled={saving}
            >
              {saving ? 'Saving...' : '💾 Save All Settings'}
            </button>
          </div>
        </div>

        <div className="info-box">
          <h3>📖 Setup Instructions</h3>
          <ol>
            <li>Go to <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer">Meta Developers</a></li>
            <li>Create or select your App</li>
            <li>Add WhatsApp product</li>
            <li>Create Business Account or link existing one</li>
            <li>Get Phone Number ID from Phone Numbers section</li>
            <li>Generate System User Access Token with <code>whatsapp_business_messaging</code> permission</li>
            <li>Set Webhook URL: <code>https://nasar.thepremium.in/api/webhook/whatsapp</code></li>
            <li>Copy Webhook Token and paste it here</li>
            <li>Save all credentials here</li>
            <li>Test by sending a message from Payments tab</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
